import { Wrench, ExternalLink } from "lucide-react";

const PHASE_LABELS = {
  warm_engine:    "Warm the Engine",
  lower_prep:     "Lower-Body Prep",
  upper_prep:     "Upper-Body Prep",
  arm_activation: "Arm Activation",
  plyo_throws:    "PlyoCare Throws",
  custom:         "Custom",
};
const TOOL_LABELS = {
  band: "Bands", plyo: "Plyo", j_band: "J-Band",
  weighted_ball: "Weighted Ball", water_stability: "Water Stability",
};

/**
 * Player-facing "Custom Routine Today" panel.
 * Renders when a coach has saved a routine_override for today.
 */
export default function CustomRoutinePanel({ override }) {
  if (!override || !override.blocks || override.blocks.length === 0) return null;
  const grouped = {};
  override.blocks.forEach((b) => {
    const key = b.phase || "custom";
    (grouped[key] = grouped[key] || []).push(b);
  });
  const phaseKeys = Object.keys(grouped).sort((a, b) => {
    const order = ["warm_engine", "lower_prep", "upper_prep", "arm_activation", "plyo_throws", "custom"];
    return order.indexOf(a) - order.indexOf(b);
  });

  return (
    <div className="rounded-2xl p-5 border" data-testid="custom-routine-panel"
      style={{ backgroundColor: "rgba(34,211,238,0.08)", borderColor: "rgba(34,211,238,0.35)" }}>
      <div className="flex items-start gap-3">
        <Wrench className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#22D3EE" }} />
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold" style={{ color: "#22D3EE" }}>
            Custom Routine · prescribed by your staff
          </div>
          <h2 className="font-display font-black text-2xl text-white mt-1 leading-tight">
            Today&apos;s personalized {override.base_protocol === "recovery" ? "recovery" : "pre-throw"} block list
          </h2>
          {override.updated_by_name && (
            <p className="text-xs text-white/55 mt-1">
              Edited by <b className="text-white/75">{override.updated_by_name}</b> · {new Date(override.updated_at).toLocaleString()}
              {override.bulk_apply_count && override.bulk_apply_count > 1 && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                  style={{ backgroundColor: "rgba(34,211,238,0.15)", color: "#67E8F9", border: "1px solid rgba(34,211,238,0.35)" }}>
                  Team-wide · {override.bulk_apply_count} athletes
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {phaseKeys.map((phase) => (
          <div key={phase}>
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/55 mb-2">
              {PHASE_LABELS[phase] || phase}
            </div>
            <div className="space-y-2">
              {grouped[phase].map((b) => (
                <div key={b.block_id} className="rounded-xl ink-card p-4" data-testid={`custom-block-${b.block_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-white text-base">{b.title}</div>
                      {b.description && <p className="text-xs text-white/65 mt-1 leading-relaxed">{b.description}</p>}
                    </div>
                    <div className="text-right text-xs font-mono-num text-white/70 flex-shrink-0">
                      {b.sets ? <div>{b.sets}<span className="text-white/40">×</span>{b.reps ?? "—"}</div> : null}
                      {b.duration_sec ? <div className="text-white/55">{b.duration_sec}s</div> : null}
                    </div>
                  </div>
                  {(b.tool_tags?.length > 0 || b.video_url) && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {(b.tool_tags || []).map((t) => (
                        <span key={t} className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border"
                          style={{ backgroundColor: "rgba(34,211,238,0.10)", borderColor: "rgba(34,211,238,0.30)", color: "#22D3EE" }}>
                          {TOOL_LABELS[t]}
                        </span>
                      ))}
                      {b.video_url && (
                        <a href={b.video_url.replace("/embed/", "/watch?v=")} target="_blank" rel="noreferrer"
                          className="ml-auto text-[11px] font-bold inline-flex items-center gap-1 hover:underline"
                          style={{ color: "#22D3EE" }}>
                          <ExternalLink className="h-3 w-3" /> Watch reference
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
